import isEmpty from "lodash/isEmpty";
import { useBookingActivityContext } from "PFApp/booking/parts/providers/booking_activity_context_provider";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { utc } from "PFCore/helpers/date";
import { useBookingsFromAvailability } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Template } from "PFTypes";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { BookingDataItem, BookingFormDataCreateMode } from "../booking_form.types";
import { truncateTitle } from "../booking_form_common_inputs.utils";
import { useClampBookingsToPastDaysLimit } from "./use_clamp_bookings_to_past_days_limit";
import { useDefaultBookingsMapper } from "./use_default_bookings_mapper";

const INITIAL_BOOKINGS_FOR_CREATE_LOADING = "initial-bookings-for-create-loading";

type UseInitialBookingsForCreate = {
  initialData: BookingFormDataCreateMode;
  enabled: boolean;
  loadingState: Record<string, boolean>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
};

type UseInitialBookingsForCreateReturn = {
  bookings: BookingDataItem[];
  globalTitle?: string;
  globalDescription?: string;
  loading: boolean;
};

export const useInitialBookingsForCreate = ({
  initialData,
  enabled,
  loadingState,
  setLoadingState
}: UseInitialBookingsForCreate): UseInitialBookingsForCreateReturn => {
  const { formatISODate } = useDateFormatter();
  const { activity: initialActivity } = useBookingActivityContext();
  const { getBookingCategory } = useBookingCategories();

  const [bookingItems, setBookingItems] = useState<BookingDataItem[]>([]);

  const { mapDefaultBookings, mapAPIBookings } = useDefaultBookingsMapper({});
  const clampToPastDaysLimit = useClampBookingsToPastDaysLimit();
  const engagementTemplate = useTemplate(TemplateKey.Engagement as Template["key"]);

  const { bookings, profileId, activityId } = initialData;

  const isBookingsFromAvailabilityEnabled =
    enabled && !!profileId && !!initialActivity && engagementTemplate?.id !== initialActivity.template_id;
  const skipBookingsFromAvailability = !isEmpty(bookings); // skip start and end dates but still use title or description
  const { data: bookingsFromAvailabilityData, isFetching: isBookingsFromAvailabilityFetching } =
    useBookingsFromAvailability({
      activityId,
      profileId,
      options: {
        enabled: isBookingsFromAvailabilityEnabled
      }
    });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const defaultEmptyBookings = mapDefaultBookings([
      {
        startDate: formatISODate(utc()),
        endDate: formatISODate(utc())
      }
    ]);

    if (isBookingsFromAvailabilityEnabled && !skipBookingsFromAvailability) {
      if (bookingsFromAvailabilityData?.ranges && !isBookingsFromAvailabilityFetching) {
        const bookingsFromAvailability = clampToPastDaysLimit(
          mapAPIBookings(bookingsFromAvailabilityData?.ranges ?? [])
        );
        setBookingItems(!isEmpty(bookingsFromAvailability) ? bookingsFromAvailability : defaultEmptyBookings);
      }
      return;
    }
    if (!isEmpty(bookings)) {
      setBookingItems(mapDefaultBookings(bookings));
      return;
    }

    setBookingItems(defaultEmptyBookings);
  }, [bookingsFromAvailabilityData, bookings, isBookingsFromAvailabilityFetching]);

  useEffect(() => {
    if (
      isBookingsFromAvailabilityEnabled &&
      !skipBookingsFromAvailability &&
      isBookingsFromAvailabilityFetching
    ) {
      setLoadingState((prev) => ({ ...prev, [INITIAL_BOOKINGS_FOR_CREATE_LOADING]: true }));
    } else {
      setLoadingState((prev) => ({ ...prev, [INITIAL_BOOKINGS_FOR_CREATE_LOADING]: false }));
    }
  }, [isBookingsFromAvailabilityEnabled, skipBookingsFromAvailability, isBookingsFromAvailabilityFetching]);

  const getGlobalTitle = () => {
    if (isBookingsFromAvailabilityEnabled && isBookingsFromAvailabilityFetching) {
      return undefined;
    }
    const categoryId = initialData?.bookingCategoryId ?? initialActivity?.booking_category_id;
    const activityName = initialActivity?.name;
    const isEngagementActivity = !!initialActivity && initialActivity?.template_id === engagementTemplate?.id;
    if (bookingsFromAvailabilityData?.title) {
      return truncateTitle(bookingsFromAvailabilityData.title);
    } else if (isEngagementActivity && activityName) {
      return truncateTitle(activityName);
    } else if (categoryId) {
      const category = getBookingCategory(categoryId);
      return category ? truncateTitle(category.display_as) : undefined;
    }
  };

  return {
    bookings: bookingItems,
    globalTitle: getGlobalTitle(),
    globalDescription: bookingsFromAvailabilityData?.description,
    loading: loadingState[INITIAL_BOOKINGS_FOR_CREATE_LOADING]
  };
};
